/**
 * SSUsers — User Account Management  (§6.4 + §6.5 complete coverage)
 *
 * Features:
 *   • List users with search + status filter
 *   • Register new user (linked to existing party OR auto-creates party)
 *   • View full detail: overview, roles, sessions
 *   • Enable / Disable / Unlock actions
 *   • Role assignment / revocation
 *   • Break-glass emergency access token issuance
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ssUserAPI, ssRoleAPI } from '../../services/ssApi';
import {
  Page, PageHeader, Card, Modal, TabBar, Section,
  Button, Badge, Field, Input, Select, Textarea,
  Spinner, EmptyState, ErrorBanner, SuccessBanner, WarnBanner, InfoBanner,
  DataTable, InfoGrid, StatBar, SearchBar,
  UserStatusBadge, TwoFaBadge,
  COLOR, FONT, RADIUS,
} from '../../components/ss';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const fmtDate = dt => dt ? new Date(dt).toLocaleString() : '\u2014';
// Backend _user_dict returns disabledaccount + lockoutenddateutc (no isactive/islocked)
const userIsDisabled = u => !!u?.disabledaccount;
const userIsLocked   = u => !!(u?.lockoutenddateutc && new Date(u.lockoutenddateutc) > new Date());
const fmtErr  = (d, fb) => {
  if (!d) return fb;
  if (typeof d === 'string') return d;
  return d.detail || d.message || JSON.stringify(d);
};

// ─────────────────────────────────────────────────────────────────────────────
// Register User modal
// ─────────────────────────────────────────────────────────────────────────────
function RegisterModal({ open, onClose, onDone }) {
  const BLANK = { party_id: '', username: '', email: '', password: '', firstname: '', lastname: '' };
  const [form, setForm]     = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');

  useEffect(() => { if (open) { setForm(BLANK); setErr(''); } }, [open]); // eslint-disable-line

  const upd = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (!form.username || !form.password) { setErr('Username and password are required.'); return; }
    setSaving(true); setErr('');
    try {
      const payload = { ...form };
      if (!payload.party_id) delete payload.party_id;
      else payload.party_id = Number(payload.party_id);
      await ssUserAPI.register(payload);
      onDone();
    } catch (ex) { setErr(fmtErr(ex.response?.data, 'Registration failed')); }
    finally { setSaving(false); }
  }

  return (
    <Modal open={open} title="Register New User" onClose={onClose} fullscreen>
      <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <ErrorBanner>{err}</ErrorBanner>
      <InfoBanner>Leave Party ID blank to auto-create a new party from first / last name.</InfoBanner>
      <form onSubmit={submit}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginTop: 14 }}>
          <Field label="Party ID (optional)" style={{ gridColumn: '1/-1', marginBottom: 10 }}>
            <Input value={form.party_id} onChange={upd('party_id')} placeholder="Link to existing party..." />
          </Field>
          <Field label="First Name" style={{ marginBottom: 10 }}>
            <Input value={form.firstname} onChange={upd('firstname')} />
          </Field>
          <Field label="Last Name" style={{ marginBottom: 10 }}>
            <Input value={form.lastname} onChange={upd('lastname')} />
          </Field>
          <Field label="Username" required style={{ marginBottom: 10 }}>
            <Input required value={form.username} onChange={upd('username')} placeholder="login name" />
          </Field>
          <Field label="Email" style={{ marginBottom: 10 }}>
            <Input type="email" value={form.email} onChange={upd('email')} />
          </Field>
          <Field label="Password" required style={{ gridColumn: '1 / span 2', marginBottom: 10 }}>
            <Input required type="password" value={form.password} onChange={upd('password')} />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? 'Registering...' : 'Register User'}</Button>
        </div>
      </form>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Disable modal
// ─────────────────────────────────────────────────────────────────────────────
function DisableModal({ user, open, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');

  useEffect(() => { if (open) { setReason(''); setErr(''); } }, [open]);

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setErr('');
    try {
      await ssUserAPI.disable(user.userid, { reason });
      onDone();
    } catch (ex) { setErr(fmtErr(ex.response?.data, 'Disable failed')); }
    finally { setSaving(false); }
  }

  return (
    <Modal open={open} title={`Disable Account \u2014 ${user?.username}`} onClose={onClose} width={460}>
      <WarnBanner>The user will be unable to log in. All active sessions will be revoked.</WarnBanner>
      <ErrorBanner>{err}</ErrorBanner>
      <form onSubmit={submit}>
        <Field label="Reason (optional)" style={{ marginBottom: 16, marginTop: 12 }}>
          <Textarea rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Left organisation" />
        </Field>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
          <Button variant="danger" type="submit" disabled={saving}>{saving ? 'Disabling...' : 'Disable Account'}</Button>
        </div>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Break-Glass modal
// ─────────────────────────────────────────────────────────────────────────────
function BreakGlassModal({ open, onClose }) {
  const BLANK = { patient_party_id: '', reason: '', duration_minutes: 120 };
  const [form, setForm]     = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');
  const [token, setToken]   = useState('');

  useEffect(() => { if (open) { setForm(BLANK); setErr(''); setToken(''); } }, [open]); // eslint-disable-line

  const upd = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (!form.patient_party_id || !form.reason) { setErr('Patient Party ID and reason are required.'); return; }
    setSaving(true); setErr('');
    try {
      const result = await ssUserAPI.breakglass({
        ...form,
        patient_party_id: Number(form.patient_party_id),
        duration_minutes: Number(form.duration_minutes),
      });
      setToken(result.data?.access_token || result.data?.token || JSON.stringify(result.data));
    } catch (ex) { setErr(fmtErr(ex.response?.data, 'Break-glass issuance failed')); }
    finally { setSaving(false); }
  }

  return (
    <Modal open={open} title="Break-Glass Emergency Access" onClose={onClose} width={560}>
      <WarnBanner>
        Emergency access is fully audited. Every request is stamped in the event log
        and a real-time notification is sent to the patient&apos;s physician and compliance officer.
      </WarnBanner>
      <ErrorBanner>{err}</ErrorBanner>

      {token ? (
        <div>
          <SuccessBanner>Break-glass token issued successfully.</SuccessBanner>
          <Section title="Access Token">
            <div style={{
              background: COLOR.grayBg,
              border: '1px solid ' + COLOR.grayBorder,
              borderRadius: RADIUS.md,
              padding: 14,
              fontSize: 11,
              wordBreak: 'break-all',
              whiteSpace: 'pre-wrap',
              maxHeight: 180,
              overflow: 'auto',
              fontFamily: 'monospace',
            }}>
              {token}
            </div>
          </Section>
          <Button style={{ marginTop: 10 }} onClick={onClose}>Close</Button>
        </div>
      ) : (
        <form onSubmit={submit} style={{ marginTop: 14 }}>
          <Field label="Patient Party ID" required style={{ marginBottom: 12 }}>
            <Input required type="number" value={form.patient_party_id} onChange={upd('patient_party_id')} placeholder="Party ID of the patient" />
          </Field>
          <Field label="Reason (mandatory)" required style={{ marginBottom: 12 }}>
            <Textarea required rows={3} value={form.reason} onChange={upd('reason')} placeholder="e.g. ER trauma intake, unconscious patient" />
          </Field>
          <Field label="Duration (minutes)" style={{ marginBottom: 18 }}>
            <Input type="number" min={5} max={480} value={form.duration_minutes} onChange={upd('duration_minutes')} />
          </Field>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
            <Button variant="danger" type="submit" disabled={saving}>{saving ? 'Issuing...' : 'Issue Emergency Token'}</Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Assign Role modal
// ─────────────────────────────────────────────────────────────────────────────
function AssignRoleModal({ user, open, onClose, onDone }) {
  const [roles, setRoles]   = useState([]);
  const [roleId, setRoleId] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');

  useEffect(() => {
    if (!open) return;
    setRoleId(''); setErr('');
    ssRoleAPI.list().then(d => setRoles(d.data || d || [])).catch(() => {});
  }, [open]);

  async function submit(e) {
    e.preventDefault();
    if (!roleId) { setErr('Select a role.'); return; }
    setSaving(true); setErr('');
    try { await ssRoleAPI.assignUser(user.userid, { role_id: Number(roleId) }); onDone(); }
    catch (ex) { setErr(fmtErr(ex.response?.data, 'Role assignment failed')); }
    finally { setSaving(false); }
  }

  return (
    <Modal open={open} title={`Assign Role \u2014 ${user?.username}`} onClose={onClose} width={420}>
      <ErrorBanner>{err}</ErrorBanner>
      <form onSubmit={submit} style={{ marginTop: 14 }}>
        <Field label="Role" required style={{ marginBottom: 18 }}>
          <Select value={roleId} onChange={e => setRoleId(e.target.value)}>
            <option value="">Select role...</option>
            {roles.map(r => (
              <option key={r.roleid} value={r.roleid}>{r.name}{r.internalofsystem ? ' (system)' : ''}</option>
            ))}
          </Select>
        </Field>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? 'Assigning...' : 'Assign Role'}</Button>
        </div>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// User Detail Panel
// ─────────────────────────────────────────────────────────────────────────────
function UserDetailPanel({ user, onClose, onRefresh }) {
  const [tab, setTab]           = useState('overview');
  const [roles, setRoles]       = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [err, setErr]           = useState('');
  const [saving, setSaving]     = useState(false);
  const [showDisable, setShowDisable]       = useState(false);
  const [showAssignRole, setShowAssignRole] = useState(false);
  const [showBreakGlass, setShowBreakGlass] = useState(false);

  const loadTab = useCallback(async () => {
    if (!user) return;
    setLoading(true); setErr('');
    try {
      if (tab === 'roles') {
        const d = await ssRoleAPI.userRoles(user.userid);
        setRoles(d.data || d || []);
      }
      if (tab === 'sessions') {
        const d = await ssUserAPI.listSessions(user.userid);
        setSessions(d.data || d || []);
      }
    } catch (ex) { setErr(fmtErr(ex.response?.data, 'Failed to load')); }
    finally { setLoading(false); }
  }, [user, tab]);

  useEffect(() => { setTab('overview'); setErr(''); }, [user]);
  useEffect(() => { loadTab(); }, [loadTab]);

  if (!user) return (
    <EmptyState icon="\U0001f464" title="Select a user" message="Click a user on the left to view their account." />
  );

  const wrap = async fn => {
    setSaving(true); setErr('');
    try { await fn(); onRefresh(); }
    catch (ex) { setErr(fmtErr(ex.response?.data, 'Action failed')); }
    finally { setSaving(false); }
  };

  const ROLE_COLS = [
    { key: 'name',             label: 'Role',        render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'description',      label: 'Description', render: v => v || <span style={{ color: COLOR.grayLight }}>\u2014</span> },
    { key: 'internalofsystem', label: 'Type',        render: v => v ? <Badge color="teal">System</Badge> : <Badge color="gray">Custom</Badge> },
    { key: '_del', label: '', render: (_, r) => (
      <Button variant="danger" size="sm" onClick={() => wrap(() => ssRoleAPI.revokeUser(user.userid, r.roleid))}>Remove</Button>
    )},
  ];

  const SESSION_COLS = [
    { key: 'sessionid',   label: 'Session',     render: v => <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{String(v).slice(0, 12)}...</span> },
    { key: 'createdat',   label: 'Created',     render: v => fmtDate(v) },
    { key: 'expiresat',   label: 'Expires',     render: v => fmtDate(v) },
    { key: 'ipaddress',   label: 'IP',          render: v => v || '\u2014' },
    { key: '_del', label: '', render: (_, s) => (
      <Button variant="danger" size="sm" onClick={() => wrap(() => ssUserAPI.deleteSession(s.sessionid))}>Revoke</Button>
    )},
  ];

  const isDisabled = userIsDisabled(user);
  const isLocked   = userIsLocked(user);
  const isActive   = !isDisabled && !isLocked;

  const TABS = [
    { id: 'overview',  label: 'Overview' },
    { id: 'roles',     label: 'Roles (' + roles.length + ')' },
    { id: 'sessions',  label: 'Sessions (' + sessions.length + ')' },
  ];

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, color: COLOR.text }}>{user.username}</div>
          <div style={{ fontSize: 12, color: COLOR.textMuted, marginTop: 2 }}>{user.email || 'No email'}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            <UserStatusBadge status={isDisabled ? 'disabled' : isLocked ? 'locked' : 'active'} />
            {user.twofactorenabled && <TwoFaBadge enabled />}
            {user.partyid && <Badge color="blue">Party #{user.partyid}</Badge>}
          </div>
        </div>
        <Button variant="ghost" onClick={onClose}>\u00d7</Button>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid ' + COLOR.graySubtle }}>
        {isDisabled && (
          <Button variant="success" size="sm" disabled={saving} onClick={() => wrap(() => ssUserAPI.enable(user.userid))}>Enable</Button>
        )}
        {isLocked && (
          <Button variant="amber" size="sm" disabled={saving} onClick={() => wrap(() => ssUserAPI.unlock(user.userid))}>Unlock</Button>
        )}
        {isActive && (
          <Button variant="danger" size="sm" onClick={() => setShowDisable(true)}>Disable</Button>
        )}
        <Button variant="ghost" size="sm" onClick={() => setShowAssignRole(true)}>+ Assign Role</Button>
        <Button variant="ghost" size="sm" onClick={() => setShowBreakGlass(true)}>Break-Glass</Button>
        <Button variant="ghost" size="sm" disabled={saving} onClick={() => wrap(() => ssUserAPI.revokeAll(user.userid))}>Revoke All Sessions</Button>
      </div>

      <ErrorBanner>{err}</ErrorBanner>

      <TabBar tabs={TABS} active={tab} onChange={t => { setTab(t); setErr(''); }} style={{ marginBottom: 16 }} />

      {tab === 'overview' && (
        <InfoGrid rows={[
          ['User ID',       user.userid],
          ['Username',      user.username],
          ['Email',         user.email || '\u2014'],
          ['Party ID',      user.partyid ?? '\u2014'],
          ['Status',        isDisabled ? 'Disabled' : isLocked ? 'Locked' : 'Active'],
          ['2FA Enabled',   user.twofactorenabled ? 'Yes' : 'No'],
          ['Created',       fmtDate(user.sys_entry_date || user.created_at)],
          ['Last Login',    fmtDate(user.lastlogindatetime)],
          ['Failed Logins', user.accessfailedcount ?? '\u2014'],
        ]} />
      )}

      {tab === 'roles' && (
        loading ? <Spinner /> : (
          <Section action={<Button size="sm" onClick={() => setShowAssignRole(true)}>+ Assign Role</Button>}>
            <DataTable
              columns={ROLE_COLS}
              rows={roles}
              rowKey="roleid"
              emptyIcon="\U0001f6e1\ufe0f"
              emptyTitle="No roles assigned"
              emptyMsg="Use Assign Role to grant access."
            />
          </Section>
        )
      )}

      {tab === 'sessions' && (
        loading ? <Spinner /> : (
          <Section action={sessions.length > 0 && (
            <Button variant="danger" size="sm" disabled={saving} onClick={() => wrap(() => ssUserAPI.revokeAll(user.userid))}>
              Revoke All
            </Button>
          )}>
            <DataTable
              columns={SESSION_COLS}
              rows={sessions}
              rowKey="sessionid"
              emptyIcon="\U0001f4bb"
              emptyTitle="No active sessions"
            />
          </Section>
        )
      )}

      <DisableModal user={user} open={showDisable} onClose={() => setShowDisable(false)} onDone={() => { setShowDisable(false); onRefresh(); }} />
      <AssignRoleModal user={user} open={showAssignRole} onClose={() => setShowAssignRole(false)} onDone={() => { setShowAssignRole(false); loadTab(); }} />
      <BreakGlassModal open={showBreakGlass} onClose={() => setShowBreakGlass(false)} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────────────────────
export default function SSUsers() {
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [err, setErr]             = useState('');
  const [search, setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected]   = useState(null);
  const [registerOpen, setRegisterOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    ssUserAPI.list()
      .then(d => { setRows(d.data || d || []); setErr(''); })
      .catch(ex => setErr(fmtErr(ex.response?.data, 'Failed to load users')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (selected) {
      const updated = rows.find(r => r.userid === selected.userid);
      if (updated) setSelected(updated);
    }
  }, [rows]); // eslint-disable-line

  const filtered = rows.filter(u => {
    const q = search.toLowerCase();
    const matchSearch = !search || (
      u.username?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q)
    );
    const isDisabled = userIsDisabled(u);
    const isLocked   = userIsLocked(u);
    const matchStatus = !statusFilter ||
      (statusFilter === 'active'   && !isDisabled && !isLocked) ||
      (statusFilter === 'disabled' && isDisabled) ||
      (statusFilter === 'locked'   && isLocked);
    return matchSearch && matchStatus;
  });

  const stats = {
    total:    rows.length,
    active:   rows.filter(u => !userIsDisabled(u) && !userIsLocked(u)).length,
    disabled: rows.filter(userIsDisabled).length,
    locked:   rows.filter(userIsLocked).length,
  };

  return (
    <Page>
      <PageHeader
        title="User Accounts"
        subtitle="Manage user accounts, roles, sessions and emergency access"
        action={<Button onClick={() => setRegisterOpen(true)}>+ Register User</Button>}
      />

      <ErrorBanner>{err}</ErrorBanner>

      <StatBar style={{ marginBottom: 20 }} stats={[
        { label: 'Total',    value: stats.total,    color: COLOR.blue },
        { label: 'Active',   value: stats.active,   color: COLOR.greenDark },
        { label: 'Disabled', value: stats.disabled, color: COLOR.redDark },
        { label: 'Locked',   value: stats.locked,   color: COLOR.amber },
      ]} />

      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 220px' }}>
          <SearchBar value={search} onChange={setSearch} placeholder="Search username or email..." />
        </div>
        <Select style={{ flex: '0 0 160px' }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
          <option value="locked">Locked</option>
        </Select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '320px 1fr' : '1fr', gap: 16 }}>
        <Card>
          {loading ? <Spinner /> : (
            filtered.length === 0
              ? <EmptyState icon="\U0001f464" title="No users found" />
              : filtered.map(u => {
                const isDisabled = userIsDisabled(u);
                const isLocked   = userIsLocked(u);
                const isSelected = selected?.userid === u.userid;
                return (
                  <div
                    key={u.userid}
                    onClick={() => setSelected(isSelected ? null : u)}
                    style={{
                      padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4,
                      background: isSelected ? COLOR.blueBg : COLOR.grayBg,
                      border: '1px solid ' + (isSelected ? COLOR.blueBorder : 'transparent'),
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13, color: COLOR.text }}>{u.username}</div>
                        {u.email && <div style={{ fontSize: 11, color: COLOR.textMuted, marginTop: 1 }}>{u.email}</div>}
                      </div>
                      <UserStatusBadge status={isDisabled ? 'disabled' : isLocked ? 'locked' : 'active'} />
                    </div>
                  </div>
                );
              })
          )}
        </Card>

        {selected && (
          <Card style={{ minHeight: 400 }}>
            <UserDetailPanel
              user={selected}
              onClose={() => setSelected(null)}
              onRefresh={load}
            />
          </Card>
        )}
      </div>

      <RegisterModal
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        onDone={() => { setRegisterOpen(false); load(); }}
      />
    </Page>
  );
}
